/**
 * File: apps/qbench-cp/reactapp/src/shared/constants/entityTypes.js
 *
 * Purpose: Defines the canonical set of LIMS entity type identifiers and related lookup
 *     enumerations used throughout the customer portal.
 *
 * Scope: Entity type, query key, print doc type, worksheet type, and comparison-operator
 *     constants shared across the customer portal codebase.
 *
 * Tags: customer-portal
 *
 * Overview: Exports `EntityType`, an enum of every entity kind the portal deals with (orders,
 *     samples, tests, sources, projects, customers, and more), used as a stable key across
 *     normalizer schemas, RBAC policies, and API calls. Also exports `PrintDocsType` and
 *     `WorksheetType` enums, a `QueryKeys` map that derives React Query cache keys per entity
 *     (some directly keyed off `EntityType` values), and `COMPARISON_OPTIONS`, an enum of
 *     filter/comparison operators used for building field-based queries.
 *
 * Exports: EntityType, PrintDocsType, QueryKeys, WorksheetType, COMPARISON_OPTIONS.
 */

export const EntityType = {
  ORDER: 'ORDER',
  ORDER_REQUEST: 'ORDER_REQUEST',
  SAMPLE: 'SAMPLE',
  TEST: 'TEST',
  SOURCE: 'SOURCE',
  PROJECT: 'PROJECT',
  BATCH: 'BATCH',
  ASSAY: 'ASSAY',
  PANEL: 'PANEL',
  PATIENT: 'PATIENT',
  CUSTOMER: 'CUSTOMER',
  SUPPLIER: 'SUPPLIER',
  CONTACT: 'CONTACT',
  LOCATION: 'LOCATION',
  ACCESSIONING_TYPE: 'ACCESSIONING_TYPE',
  EQUIPMENT: 'EQUIPMENT',
  INVENTORY_ITEM: 'INVENTORY_ITEM',
  INVENTORY_STOCK: 'INVENTORY_STOCK',
  USER: 'USER',
  WORKSHEET: 'WORKSHEET',
  PROTOCOL: 'PROTOCOL',
  GENERIC_LOOKUP: 'GENERIC_LOOKUP',
  TEAM: 'TEAM',
  SCHEDULE: 'SCHEDULE',
  TURNAROUND: 'TURNAROUND',
  INVOICE: 'INVOICE',
  ISSUE: 'ISSUE',
  QUOTATION: 'QUOTATION',
  QUOTATION_ITEM: 'QUOTATION_ITEM',
  STATUSES: 'STATUSES',
  REPORT: 'REPORT',
  DIVISION: 'DIVISION',
}

export const PrintDocsType = {
	CHAIN_OF_CUSTODY: 'chain of custody',
	LABELS: 'labels',
	SHIPPING_MANIFEST: 'shipping manifest',
};

export const QueryKeys = {
  ASSAY: 'assays',
  PANEL: 'panels',
  SOURCE: 'sources',
  PRICING: 'pricing',
  QUOTATION: 'quotations',
  TURNAROUND: 'turnarounds',
  PROJECTS: 'projects',
  REPORTS: 'reports',
  [EntityType.SAMPLE]: 'samples',
  [EntityType.TEST]: 'tests',
  [EntityType.CONTACT]: 'contacts',
  [EntityType.DIVISION]: 'divisions',
  [EntityType.USER]: 'users',
  [EntityType.CUSTOMER]: 'customers',
  [EntityType.PROJECT]: 'projects',
  [EntityType.TURNAROUND]: 'turnarounds',
  [EntityType.SOURCE]: 'sources',
  [EntityType.ACCESSIONING_TYPE]: 'accessioningTypes',
};

export const WorksheetType = {
	QWML: 'QWML',
	SPREADSHEET: 'SPREADSHEET',
  DYNAMIC_SPREADSHEET: 'DYNAMIC_SPREADSHEET',
};

export const COMPARISON_OPTIONS = {
  IS_EMPTY: 'IS_EMPTY',
  IS_NOT_EMPTY: 'IS_NOT_EMPTY',
  TEXT_CONTAINS: 'TEXT_CONTAINS',
  TEXT_DOES_NOT_CONTAIN: 'TEXT_DOES_NOT_CONTAIN',
  TEXT_STARTS_WITH: 'TEXT_STARTS_WITH',
  TEXT_ENDS_WITH: 'TEXT_ENDS_WITH',
  TEXT_IS_EXACTLY: 'TEXT_IS_EXACTLY',
  GREATER_THAN: 'GREATER_THAN',
  GREATER_THAN_OR_EQUAL_TO: 'GREATER_THAN_OR_EQUAL_TO',
  LESS_THAN: 'LESS_THAN',
  LESS_THAN_OR_EQUAL_TO: 'LESS_THAN_OR_EQUAL_TO',
  IS_EQUAL_TO: 'IS_EQUAL_TO',
  IS_NOT_EQUAL_TO: 'IS_NOT_EQUAL_TO',
  IS_BETWEEN: 'IS_BETWEEN',
  IS_NOT_BETWEEN: 'IS_NOT_BETWEEN',
  IS_BETWEEN_INCLUSIVE: 'IS_BETWEEN_INCLUSIVE',
  IS_BETWEEN_STRICT_UPPER: 'IS_BETWEEN_STRICT_UPPER',
  IS_BETWEEN_STRICT_LOWER: 'IS_BETWEEN_STRICT_LOWER',
  IS_NOT_BETWEEN_INCLUSIVE: 'IS_NOT_BETWEEN_INCLUSIVE',
  IS_NOT_BETWEEN_STRICT_UPPER: 'IS_NOT_BETWEEN_STRICT_UPPER',
  IS_NOT_BETWEEN_STRICT_LOWER: 'IS_NOT_BETWEEN_STRICT_LOWER',
  CUSTOM_FORMULA_IS: 'CUSTOM_FORMULA_IS',
};
